const API_BASE = process.env.REACT_APP_API_BASE || '';

// Session token storage keys
const TOKEN_STORAGE_KEY = 'admin_session_token';
const TOKEN_EXPIRY_KEY = 'admin_session_expiry';

// Session duration: 24 hours in milliseconds
const SESSION_DURATION_MS = 24 * 60 * 60 * 1000;

// Store token in localStorage with expiry time
export function setSessionToken(token: string | null) {
    if (token) {
        try {
            localStorage.setItem(TOKEN_STORAGE_KEY, token);
            // Set expiry time to 24 hours from now
            const expiryTime = Date.now() + SESSION_DURATION_MS;
            localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
        } catch (e) {
            console.warn('Failed to store session token:', e);
        }
    } else {
        try {
            localStorage.removeItem(TOKEN_STORAGE_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_KEY);
        } catch (e) {
            console.warn('Failed to remove session token:', e);
        }
    }
}

export function getSessionToken(): string | null {
    try {
        const token = localStorage.getItem(TOKEN_STORAGE_KEY);
        const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);

        if (!token || !expiryStr) {
            return null;
        }

        const expiryTime = parseInt(expiryStr, 10);

        // Check if session has expired
        if (Date.now() > expiryTime) {
            // Session expired, clear storage
            localStorage.removeItem(TOKEN_STORAGE_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_KEY);
            return null;
        }

        return token;
    } catch (e) {
        return null;
    }
}

// Check if there's a valid session stored
export function hasValidSession(): boolean {
    return getSessionToken() !== null;
}

interface RequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: any;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body } = options;

    const headers: HeadersInit = {
        'Accept': 'application/json',
    };

    if (body) {
        headers['Content-Type'] = 'application/json';
    }

    // Add Authorization header as fallback for iOS Safari when cookies fail
    const token = getSessionToken();
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    try {
        const response = await fetch(`${API_BASE}${endpoint}`, {
            method,
            headers,
            credentials: 'include',
            // iOS Safari sometimes needs explicit cache control
            cache: 'no-cache',
            body: body ? JSON.stringify(body) : undefined,
        });

        // Handle non-JSON responses gracefully
        const contentType = response.headers.get('content-type');
        let data: any;

        if (contentType && contentType.includes('application/json')) {
            data = await response.json();
        } else {
            const text = await response.text();
            console.error('Non-JSON response:', text.substring(0, 200));
            throw new Error('Server returned non-JSON response');
        }

        // Store token from response header (iOS Safari fallback)
        const responseToken = response.headers.get('X-Session-Token');
        if (responseToken) {
            setSessionToken(responseToken);
        }

        if (!response.ok) {
            // Clear token on auth failure
            if (response.status === 401) {
                setSessionToken(null);
            }
            throw new Error(data.error || `Request failed with status ${response.status}`);
        }

        return data;
    } catch (error: any) {
        // Enhanced error logging for debugging iOS issues
        console.error('API request failed:', {
            endpoint,
            method,
            error: error.message,
            // Check for network/CORS errors common on iOS
            isNetworkError: error.message === 'Failed to fetch' || error.message === 'Load failed',
        });
        throw error;
    }
}

export const api = {
    get: <T>(endpoint: string) => request<T>(endpoint),
    post: <T>(endpoint: string, body?: any) => request<T>(endpoint, { method: 'POST', body }),
    put: <T>(endpoint: string, body?: any) => request<T>(endpoint, { method: 'PUT', body }),
    delete: <T>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),
};
